import * as Sentry from '@sentry/react';
import { getEnv } from './utils';

/* *************** */
/*   Token Names   */
/* *************** */

/** The DEFAULT_TOKEN should enable service cost searches with the NCCT api. The prefix set on the member_id is STA. */
export const DEFAULT_TOKEN = 'Default Token (Deductible Met)' as const;
export const INVALID_SNAPSHOT = 'Invalid coverage snapshot' as const;
export const SOCT_1 =
  '(OOP NOT MET) SOCT Standard Plan, $0 Deductible (preferredaccessoutofarea)' as const;
export const SOCT_2 =
  '(OOP MET) SOCT Quality First Select Access, $0 deductible (qualityfirstselectaccess)' as const;
export const NG_1 =
  '(NOTHING MET) NG Medium Premium / Medium Deductible (northropgrumman-bluecardppo)' as const;
export const NG_2 =
  '(DED MET) NG High Premium / Low Deductible (northropgrumman-floridaselect)' as const;
export const NG_3 =
  '(OOP MET) NG Medium Premium / Medium Deductible (Utah) (northropgrumman-bluecardppo)' as const;
export const NG_4 = 'NG Low Premium/High Deductible (northropgrumman-cmdselect)' as const;
export const NG_5 = 'NG Baltimore Premium Plus Plan (northropgrumman-bluecardppo)' as const;
export const NG_6 = 'NG Value Plan - Sunnyvale (northropgrumman-bluecardppo)' as const;
export const NG_7 = 'NG Premium - Sunnyvale (northropgrumman-bluecardppo)' as const;
export const NG_8 = 'NG Value Plan - Baltimore (northropgrumman-bluecardppo)' as const;
export const NG_9 = 'NG Premium - Baltimore plan config (northropgrumman-bluecardppo)' as const;

type DemoTokenNames =
  | typeof DEFAULT_TOKEN
  | typeof INVALID_SNAPSHOT
  | typeof SOCT_1
  | typeof SOCT_2
  | typeof NG_1
  | typeof NG_2
  | typeof NG_3
  | typeof NG_4
  | typeof NG_5
  | typeof NG_6
  | typeof NG_7
  | typeof NG_8
  | typeof NG_9;

type DemoTokenMap = Partial<Record<DemoTokenNames, string>>;

// For demo client cost testing, tokens will have member_id 'STAtest' and network_id 'demo1234'.
/* ************** */
/*  Environments  */
/* ************** */
export const testing = {
  [DEFAULT_TOKEN]: '258097d6-d484-4950-b543-a5b26d233d97',
  [INVALID_SNAPSHOT]: '4354615f-b082-490f-a32a-29e12286ef1f',
  [SOCT_1]: '226fa9e7-b0c5-46f4-8703-c4cf4616855e',
  [SOCT_2]: '02be48d2-bd0a-430b-affc-157d6f5d169e',
  [NG_1]: 'c860a062-4615-4c4c-9d4c-fbfb9a7e81c7',
  [NG_2]: '950bd6a7-1942-4a35-a6e7-316282638083',
  [NG_3]: '5bc0154d-b896-4f6d-a608-d2f4cca00545',
  [NG_4]: '402ecdd7-946e-4643-ad42-a80ba1d47022',
  [NG_5]: '81fafee3-2344-4c82-9d57-701cac1155c1',
  [NG_6]: 'c2c71772-ccce-452c-99e4-625da8a05d65',
  [NG_7]: '3dbc6bbf-50dc-4c62-aeb7-c2922f87bc4d',
  [NG_8]: 'b87b09d6-ffb6-4b27-8d7e-361d9b222542',
  [NG_9]: '786e9e48-eebc-46ef-bdf8-b34527f66eaf',
};

const staging: DemoTokenMap = {
  [DEFAULT_TOKEN]: '87ee4c04-1043-47e3-914e-e92adb89cb46',
};

const production: DemoTokenMap = {
  [DEFAULT_TOKEN]: '3ad6cb20-dc81-4514-bd0f-579d01371d92',
};

type DemoTokenEnvMap = Record<'testing' | 'staging' | 'production', DemoTokenMap>;

/* ************ */
/*   Token Map  */
/* ************ */
const demoTokens: DemoTokenEnvMap = {
  testing,
  staging,
  production,
};

/**
 * Function that sets an integration token based on the tokenName parameter passed into localStorage.
 * The tokenName parameter must be one of the constants in demoTokens.js.
 * If there is no matching token found, we will continue to use the current integration token.
 * @example setDemoToken(DEFAULT_TOKEN)
 */
export const setDemoToken = (tokenName: DemoTokenNames): void => {
  let env = getEnv();
  if (env === 'local') env = 'testing';
  const token = demoTokens?.[env]?.[tokenName];
  if (token) localStorage.setItem('integrationToken', token);
  else
    Sentry.captureMessage(`Unable to set Demo integration token from name: ${tokenName}`, (scope) =>
      scope.setLevel('warning')
    );
};
